import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Paperclip, Upload } from "lucide-react";
import api, { errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { php, date, label } from "@/lib/format";
import { PageHeader, MarketSelect, SelectBox, StatusBadge, DemoTag, Empty } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import LeadDrawer from "@/components/leads/LeadDrawer";

const STATUSES = ["draft", "pending_approval", "approved", "rejected", "sent", "accepted", "declined"];
const ACTIONS = {
  draft: [["pending_approval", "Submit for approval", "create"], ["sent", "Mark sent", "create"]],
  pending_approval: [["approved", "Approve", "approve"], ["rejected", "Reject", "approve"]],
  rejected: [["draft", "Revise", "create"]],
  approved: [["sent", "Mark sent", "create"]],
  sent: [["accepted", "Accepted", "create"], ["declined", "Declined", "create"]],
  declined: [["draft", "Revise", "create"]],
};

function Actions({ q, reload }) {
  const { can } = useAuth();
  const act = async (status) => {
    try {
      await api.post(`/quotations/${q.id}/status`, { status });
      toast.success(`Quotation ${label(status).toLowerCase()}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/quotations/${q.id}/attachments`, fd);
      toast.success("Attachment uploaded");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
      {(ACTIONS[q.status] || []).filter(([, , p]) => can(`quotations.${p}`)).map(([s, l]) => (
        <Button key={s} size="sm" variant={s === "approved" ? "default" : "outline"} className={s === "approved" ? "bg-emerald-600 hover:bg-emerald-700 h-7" : "h-7"} onClick={() => act(s)} data-testid={`quote-action-${s}-${q.id}`}>{l}</Button>
      ))}
      {can("quotations.create") && (
        <label className="inline-flex items-center h-7 px-2 border rounded text-xs cursor-pointer hover:bg-slate-50" data-testid={`quote-upload-${q.id}`}>
          <Upload className="h-3 w-3 mr-1" />Attach<input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.docx" onChange={upload} />
        </label>
      )}
    </div>
  );
}

export default function Quotations() {
  const { can, marketName, meta } = useAuth();
  const [status, setStatus] = useState("");
  const [market, setMarket] = useState("");
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/quotations", { params: { status: status || undefined, market: market || undefined } });
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [status, market]);
  useEffect(() => { load(); }, [load]);

  const total = rows.reduce((a, q) => a + q.amount, 0);
  const threshold = Number(meta.settings.quotation_approval_threshold || 0);

  return (
    <div data-testid="quotations-page">
      <PageHeader eyebrow="Commercial" title="Quotations" subtitle={`Amount, status, reference number and attachments. ${threshold > 0 ? `Quotations of ${php(threshold)} and above` : "All quotations"} require approval before sending — never automatic.`}>
        <MarketSelect value={market} onChange={setMarket} />
        <SelectBox value={status} onChange={setStatus} testid="quote-status-filter" allLabel="All statuses" options={STATUSES.map((s) => ({ value: s, label: label(s) }))} />
        {can("reports.export") && <Button variant="outline" onClick={() => downloadFile("/export/quotations.csv", "salescore_quotations.csv").catch((e) => toast.error(errMsg(e)))} data-testid="quotes-export-button"><Download className="h-4 w-4 mr-1" />Export CSV</Button>}
      </PageHeader>
      <div className="text-sm text-slate-500 mb-3" data-testid="quotes-total">{rows.length} quotation(s) · Total <span className="num font-semibold text-[#0B192C]">{php(total)}</span></div>
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Ref no.</TableHead><TableHead>Lead</TableHead><TableHead>Market</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Files</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((q) => (
              <TableRow key={q.id} className="cursor-pointer" onClick={() => setOpenId(q.lead_id)} data-testid={`quote-row-${q.id}`}>
                <TableCell className="num text-sm font-medium">{q.ref_no}<DemoTag show={q.is_demo} /></TableCell>
                <TableCell><div className="text-sm">{q.lead_name}</div><div className="text-xs text-slate-500">{q.lead_company}</div></TableCell>
                <TableCell className="text-xs">{marketName(q.market)}</TableCell>
                <TableCell className="text-right num whitespace-nowrap">{php(q.amount)}</TableCell>
                <TableCell><StatusBadge status={q.status} testid={`quote-status-${q.id}`} />{q.approved_by && <div className="text-[11px] text-slate-400 mt-1">by {q.approved_by}</div>}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {q.attachments.map((a) => (
                    <button key={a.id} className="flex items-center gap-1 text-xs text-blue-600 hover:underline" onClick={() => downloadFile(`/files/${a.id}`, a.filename).catch((e) => toast.error(errMsg(e)))} data-testid={`attachment-${a.id}`}>
                      <Paperclip className="h-3 w-3" />{a.filename}
                    </button>
                  ))}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">{date(q.created_at)}</TableCell>
                <TableCell><Actions q={q} reload={load} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <Empty>No quotations yet. Create one from a lead's Quotes tab.</Empty>}
      </div>
      <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
